/** @jsxImportSource @emotion/react */

import classNames from 'classnames';
import { resetAuthChecking } from 'modules/auth/store';
import React, { useCallback } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { useDispatch } from 'react-redux';
import { LANDING_URL, PRIVACY_POLICY_URL, TERMS_AND_CONDITIONS_URL } from 'shared/constants/links';
import type { AppDispatch } from 'store/store';
import { DivProps } from 'typings';
import UDLink from 'ud-ui/components/link';
import UDLogo from 'ud-ui/components/logo';
import UDText from 'ud-ui/components/text';

import AuthComponentsBillboard from '../components/billboard';
import * as S from './styles';

type Props = {
  title: string;
  subtitle?: string;
  action?: {
    title: string;
    linkLabel: string;
    link: string;
  };
  children: React.ReactNode;
  contentClassName?: string;
  formContainerProps?: DivProps;
  showPrivacyPolicy?: boolean;
  logoTo: string;
};

const AuthLayout = (props: Props) => {
  const { t } = useLingui();
  const dispatch = useDispatch<AppDispatch>();
  const resetCode = useCallback(() => {
    dispatch(resetAuthChecking());
  }, [dispatch]);

  return (
    <S.Container>
      <div className="left">
        <div className="left-container">
          <UDLogo to={props.logoTo} className="header block" />
          <div className="content">
            <S.FormContainer {...props.formContainerProps}>
              {(props.title || props.subtitle) && (
                <div className="header flex flex-col justify-between" style={{ gap: 10, marginBottom: 30 }}>
                  {props.title && (
                    <UDText type="title-1" className="text-surfaceWhite" style={{ fontFamily: 'SF Pro Text', fontWeight: 300 }}>
                      {props.title}
                    </UDText>
                  )}
                  {props.subtitle && (
                    <UDText
                      type="callout"
                      style={{ fontFamily: 'SF Pro Text', lineHeight: '18px' }}
                      className="subtitle text-surfaceWhite"
                    >
                      {props.subtitle}
                    </UDText>
                  )}
                </div>
              )}
              <div className={classNames(props.contentClassName)}>{props.children}</div>
              {props.action && (
                <div className="mt-auto">
                  <S.AlreadyContainer>
                    <UDText type="subhead" className="text-surfaceWhite">
                      {props.action.title}
                    </UDText>
                    <UDLink to={props.action.link} type="subhead" className="text-accentNaplesYellow" onClick={resetCode}>
                      {props.action.linkLabel}
                    </UDLink>
                  </S.AlreadyContainer>
                </div>
              )}
              {props.showPrivacyPolicy && (
                <UDText variant="subtitle-1" className="text-Gray text-center" style={{ marginTop: 32 }}>
                  {t`Нажимая на кнопку «Создать аккаунт» я понимаю, что заключаю договор в соответствии с `}
                  <a
                    href="https://imgameapp.ru/offer"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link typo subtitle-1 muted underline"
                  >
                    {t`условиями оферты`}
                  </a>
                  {t`, согласен и ознакомлен с её условиями, а также ознакомлен и согласен с `}
                  <a
                    href={PRIVACY_POLICY_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="link typo subtitle-1 muted underline"
                  >
                    {t`политикой в отношении обработки персональных данных`}
                  </a>
                  {t` и выражаю своё согласие на обработку и передачу персональных данных.`}
                </UDText>
              )}
            </S.FormContainer>
          </div>
          <div className="footer">
            <a className="link text subhead mr-6" href={LANDING_URL} target="_blank" rel="noreferrer">
              <Trans>О приложении</Trans>
            </a>
            <a className="link text subhead mr-6" href={PRIVACY_POLICY_URL} target="_blank" rel="noreferrer">
              <Trans>Политика конфиденциальности</Trans>
            </a>
            <a className="link text subhead mr-6" href={TERMS_AND_CONDITIONS_URL} target="_blank" rel="noreferrer">
              <Trans>Условия использования</Trans>
            </a>
          </div>
        </div>
      </div>
      <AuthComponentsBillboard className="right" />;
    </S.Container>
  );
};

export default AuthLayout;
